<template>
  <div class="properties-content">
    <div class="config-section">
      <!-- 行为拒止使能开关 -->
      <div class="property-item behavior-prohibition-item">
        <div class="property-label">
          <svg-icon name="shield" class="property-icon"></svg-icon>
          {{$translate('behaviorProhibitionEnable')}}
        </div>
        <div class="property-switch">
          <i-switch 
            v-model="behaviorProhibitionEnabled" 
            size="large"
            @on-change="handleBehaviorProhibitionChange"
            :loading="switchLoading"
            :disabled="disabledStatus"
          >
            <span slot="open">{{$translate('enabled')}}</span>
            <span slot="close">{{$translate('disabled')}}</span>
          </i-switch>
        </div>
      </div>

      <!-- 低电量阈值配置 -->
      <div class="property-item">
        <div class="property-label">
          <svg-icon name="battery" class="property-icon"></svg-icon>
          {{$translate('lowBatteryThreshold')}}
        </div>
        <div class="property-slider" @wheel.prevent="handleWheel">
          <Slider 
            v-model="lowBattery" 
            :min="20"
            :max="40"
            :step="1"
            :disabled="disabledStatus"
            @on-change="handleLowBatteryChange"
          >
          </Slider>
          <div class="slider-description" :class="{ 'disabled': disabledStatus }">
            <span class="battery-icon">🔋</span>
            <span class="value-text">{{$translate('currentThreshold')}}: <strong>{{lowBattery}}%</strong></span>
          </div>
        </div>
      </div>

      <!-- 相机视角配置 -->
      <div class="property-item camera-config-item">
        <div class="property-label">
          <div class="label-left">
            <svg-icon name="camera" class="property-icon"></svg-icon>
            {{$translate('cameraViewConfig')}}
          </div>
          <div class="label-right" v-if="selectedCameras.length > 0">
            <span class="selected-count">
              <Icon type="ios-videocam" />
              {{$translate('selectedCameras')}}: <strong>{{selectedCameras.length}}</strong>
            </span>
          </div>
        </div>
        <div class="camera-selection">
          <div 
            v-for="camera in cameraOptions" 
            :key="camera.value"
            class="camera-card"
            :class="{ 
              'camera-card-selected': selectedCameras.includes(camera.value)
            }"
            @click="toggleCamera(camera.value)"
          >
            <Icon type="ios-videocam" class="camera-card-icon" />
            <div class="camera-card-label">{{ camera.label }}</div>
            <Icon type="ios-checkmark-circle" class="camera-card-check" v-if="selectedCameras.includes(camera.value)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'SmartConfig',
  props: {
    selectedRobot: {
      type: Object,
      default: () => ({})
    },
    selectedRobotId: {
      type: [String, Number],
      default: null
    },
    disabledStatus:{
      type:Boolean,
      default:false
    }
  },
  data() {
    return {
      behaviorProhibitionEnabled: false,
      switchLoading: false,
      lastUpdateTime: '--',
      lowBattery: 20,
      previousLowBattery: 20,
      wheelUpdateTimer: null,
      selectedCameras: [],
      cameraOptions: [
        { label: this.$translate('leftFront'), value: 0 },
        { label: this.$translate('leftRear'), value: 1 },
        { label: this.$translate('rightFront'), value: 2 },
        { label: this.$translate('rightRear'), value: 3 },
        { label: this.$translate('leftFrontRightRear'), value: 4 },
        { label: this.$translate('leftRearRightFront'), value: 5 },
        { label: this.$translate('frontViewStitch'), value: 6 },
        { label: this.$translate('rearViewStitch'), value: 7 },
        { label: this.$translate('infraredLeftRear'), value: 8 },
        { label: this.$translate('infraredRightFront'), value: 9 }
      ]
    }
  },
  watch: {
    selectedRobotId: {
      handler(newVal) {
        if (newVal) {
          this.loadBehaviorProhibitionStatus();
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载行为拒止状态
    loadBehaviorProhibitionStatus() {
      if (!this.selectedRobotId) return;
      this.behaviorProhibitionEnabled = this.selectedRobot.pursuitFuncEnable || false;
      this.lowBattery = this.selectedRobot.lowBattery || 20;
      this.previousLowBattery = this.lowBattery;
      this.loadCameraConfig();
    },

    // 加载相机配置
    loadCameraConfig() {
      if (!this.selectedRobotId) return;
      
      api.getData(
        `robot-server/camera/switch/config/get?robotCode=${this.selectedRobot.code}`,
        this
      ).then(res => {
        if (res.state === 1 && res.data) {
          // 解析相机类型字符串，例如 "0,2,4"
          if (res.data.cameraTypes) {
            this.selectedCameras = res.data.cameraTypes.split(',').map(Number);
          }
        }
      }).catch(() => {
        // 加载失败时使用默认值
        this.selectedCameras = [];
      });
    },

    // 切换相机选择
    toggleCamera(value) {
      if (!this.selectedRobotId) {
        this.$Notice.warning({
          title: this.$translate('warning'),
          desc: this.$translate('pleaseSelectRobot')
        });
        return;
      }

      const index = this.selectedCameras.indexOf(value);
      if (index > -1) {
        this.selectedCameras.splice(index, 1);
      } else {
        this.selectedCameras.push(value);
      }
      
      this.updateCameraConfig(this.selectedCameras);
    },

    // 更新相机配置
    updateCameraConfig(cameraTypes) {
      const cameraTypesStr = cameraTypes.join(',');
      
      api.postData(
        'robot-server/camera/switch/config/save',
        {
          robotCode: this.selectedRobot.code,
          cameraTypes: cameraTypesStr
        },
        this
      ).then(res => {
        if (res.state === 0) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg || this.$translate('operationFailed')
          });
          // 恢复原配置
          this.loadCameraConfig();
        } else {
          this.$Notice.success({
            title: this.$translate('success'),
            desc: this.$translate('cameraConfigUpdated')
          });
        }
      }).catch(() => {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('operationFailed')
        });
        // 恢复原配置
        this.loadCameraConfig();
      });
    },

    // 处理行为拒止开关变化
    handleBehaviorProhibitionChange(value) {
      if (!this.selectedRobotId) {
        this.$Notice.warning({
          title: this.$translate('warning'),
          desc: this.$translate('pleaseSelectRobot')
        });
        this.behaviorProhibitionEnabled = !value;
        return;
      }

      this.$Modal.confirm({
        title: this.$translate('confirmOperation'),
        content: value 
          ? this.$translate('confirmEnableBehaviorProhibition')
          : this.$translate('confirmDisableBehaviorProhibition'),
        onOk: () => {
          this.updateBehaviorProhibitionStatus(value);
        },
        onCancel: () => {
          // 取消时恢复原状态
          this.behaviorProhibitionEnabled = !value;
        }
      });
    },

    // 更新行为拒止状态
    updateBehaviorProhibitionStatus(enabled) {
      this.switchLoading = true;
      
      api.postData(
        'notice-server/cmd/config/pursuit',
        {
          code: this.selectedRobot.code,
          data: {
            pursuitFuncEnable: enabled,
          }
        },
        this
      ).then(res => {
        this.switchLoading = false;
        if (res.state === 0) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg || this.$translate('operationFailed')
          });
          // 恢复原状态
          this.behaviorProhibitionEnabled = !enabled;
        } else {
          this.$Notice.success({
            title: this.$translate('success'),
            desc: enabled 
              ? this.$translate('behaviorProhibitionEnabled')
              : this.$translate('behaviorProhibitionDisabled')
          });
          this.lastUpdateTime = new Date().toLocaleString('zh-CN');
        }
      }).catch(() => {
        this.switchLoading = false;
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('operationFailed')
        });
        // 恢复原状态
        this.behaviorProhibitionEnabled = !enabled;
      });
    },

    // 处理鼠标滚轮调整
    handleWheel(event) {
      if (this.disabledStatus) return;
      
      const delta = event.deltaY > 0 ? -1 : 1;
      let newValue = this.lowBattery + delta;
      
      if (newValue < 0) newValue = 0;
      if (newValue > 50) newValue = 50;
      
      if (newValue !== this.lowBattery) {
        this.lowBattery = newValue;
        
        // 清除之前的定时器
        if (this.wheelUpdateTimer) {
          clearTimeout(this.wheelUpdateTimer);
        }
        
        // 延时800ms更新
        this.wheelUpdateTimer = setTimeout(() => {
          this.handleLowBatteryChange(newValue);
        }, 800);
      }
    },

    // 处理低电量阈值变化
    handleLowBatteryChange(value) {
      if (!this.selectedRobotId) {
        this.$Notice.warning({
          title: this.$translate('warning'),
          desc: this.$translate('pleaseSelectRobot')
        });
        return;
      }

      this.updateLowBatteryThreshold(value);
    },

    // 保存当前值作为旧值
    savePreviousLowBattery() {
      this.previousLowBattery = this.lowBattery;
    },

    // 更新低电量阈值
    updateLowBatteryThreshold(value) {
      const oldValue = this.previousLowBattery;
      
      api.postData(
        'notice-server/cmd/config/lowBattery',
        {
          code: this.selectedRobot.code,
          data: {
            lowBattery: value
          }
        },
        this
      ).then(res => {
        if (res.state === 0) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: res.msg || this.$translate('operationFailed')
          });
          // 恢复原值
          this.lowBattery = oldValue;
        } else {
          this.$Notice.success({
            title: this.$translate('success'),
            desc: `${this.$translate('lowBatteryThresholdUpdated')} ${value}%`
          });
          // 更新成功后保存当前值
          this.previousLowBattery = value;
        }
      }).catch(() => {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('operationFailed')
        });
        // 恢复原值
        this.lowBattery = oldValue;
      });
    }
  }
}
</script>

<style lang="less" scoped>
.properties-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.properties-content::-webkit-scrollbar {
  width: 6px;
}

.properties-content::-webkit-scrollbar-track {
  background: rgba(0, 191, 255, 0.1);
  border-radius: 3px;
}

.properties-content::-webkit-scrollbar-thumb {
  background: rgba(0, 191, 255, 0.3);
  border-radius: 3px;
}

.properties-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 191, 255, 0.5);
}

.config-section {
  margin-bottom: 20px;
  padding: 20px;
  background: rgba(15, 35, 70, 0.4);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 12px;
}

.property-item {
  margin-bottom: 15px;
  padding: 20px;
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.property-item:hover {
  border-color: rgba(0, 191, 255, 0.4);
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.1);
}

.behavior-prohibition-item {
  background: rgba(15, 35, 70, 0.8);
  border-color: rgba(255, 193, 7, 0.3);
}

.behavior-prohibition-item:hover {
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 0 20px rgba(255, 193, 7, 0.1);
}

.property-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 15px;
}

.label-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.label-right {
  display: flex;
  align-items: center;
}

.selected-count {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  background: rgba(0, 255, 136, 0.12);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 12px;
}

.selected-count i {
  font-size: 14px;
  color: rgba(0, 255, 136, 0.8);
}

.selected-count strong {
  color: #00ff88;
  font-size: 14px;
  font-weight: 600;
  margin: 0 2px;
}

.property-icon {
  font-size: 18px;
  color: #00bfff;
}

.property-switch {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.property-slider {
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: pointer;
  user-select: none;
}
/deep/ .ivu-slider-button-wrap{
  top:-7px
}
.slider-description {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  color: #00bfff;
  font-weight: 500;
  text-align: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.15), rgba(0, 255, 136, 0.1));
  border: 1px solid rgba(0, 191, 255, 0.4);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 191, 255, 0.2);
  transition: all 0.3s ease;
}

.slider-description:hover {
  border-color: rgba(0, 191, 255, 0.6);
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.3);
  transform: translateY(-1px);
}

.slider-description.disabled {
  background: linear-gradient(135deg, rgba(128, 128, 128, 0.15), rgba(100, 100, 100, 0.1));
  border-color: rgba(128, 128, 128, 0.3);
  box-shadow: none;
  cursor: not-allowed;
}

.slider-description.disabled:hover {
  border-color: rgba(128, 128, 128, 0.3);
  box-shadow: none;
  transform: none;
}

.slider-description.disabled .battery-icon {
  filter: grayscale(1) opacity(0.5);
  animation: none;
}

.slider-description.disabled .value-text {
  color: rgba(128, 128, 128, 0.7);
}

.slider-description.disabled .value-text strong {
  color: rgba(128, 128, 128, 0.9);
  text-shadow: none;
}

.battery-icon {
  font-size: 20px;
  filter: drop-shadow(0 0 8px rgba(0, 255, 136, 0.6));
  animation: battery-pulse 2s ease-in-out infinite;
}

.value-text {
  color: rgba(255, 255, 255, 0.9);
}

.value-text strong {
  color: #00ff88;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
}

@keyframes battery-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

.switch-description {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  padding: 8px 12px;
  background: rgba(0, 191, 255, 0.05);
  border-left: 3px solid rgba(0, 191, 255, 0.3);
  border-radius: 4px;
}

.property-value.readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.value-display {
  font-size: 16px;
  font-weight: 500;
  color: #00bfff;
  padding: 10px 15px;
  background: rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 8px;
  flex: 1;
}

.status-info {
  margin-top: 15px;
  padding: 15px;
  background: rgba(0, 255, 136, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.status-indicator.active {
  color: #00ff88;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #00ff88;
  box-shadow: 0 0 10px rgba(0, 255, 136, 0.6);
  animation: pulse-green 2s infinite;
}

.status-value {
  font-size: 14px;
  color: #00bfff;
  font-weight: 500;
}

/* 动画 */
@keyframes pulse-green {
  0% {
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.6);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.9);
  }
  100% {
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.6);
  }
}

/* Switch组件样式定制 */
/deep/ .ivu-switch-large {
  width: 90px;
  height: 22px;
  line-height: 20px;
}
/deep/ .ivu-switch-large.ivu-switch-checked:after {
    left: 70px;
}
/deep/ .ivu-switch-large.ivu-switch-checked {
  background: linear-gradient(135deg, #00ff88, #00cc6a);
  border-color: #00ff88;
}

/deep/ .ivu-switch-large:not(.ivu-switch-checked) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

/deep/ .ivu-switch-large .ivu-switch-inner {
  font-size: 12px;
  font-weight: 500;
}

/* Slider组件样式定制 */
/deep/ .ivu-slider {
  margin: 10px 0;
}

/deep/ .ivu-slider-bar {
  background: linear-gradient(90deg, #00bfff, #00ff88);
  box-shadow: 0 0 10px rgba(0, 191, 255, 0.5);
}

/deep/ .ivu-slider-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

/deep/ .ivu-slider-button {
  width: 18px;
  height: 18px;
  border: 3px solid #00ff88;
  background: #0a1929;
  box-shadow: 0 0 15px rgba(0, 255, 136, 0.6);
}

/deep/ .ivu-slider-button:hover {
  border-color: #00bfff;
  box-shadow: 0 0 20px rgba(0, 191, 255, 0.8);
  transform: scale(1.2);
}

/deep/ .ivu-slider:hover .ivu-slider-bar {
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.7);
}

/deep/ .ivu-slider-disabled .ivu-slider-bar {
  background: rgba(128, 128, 128, 0.3) !important;
  box-shadow: none !important;
}

/deep/ .ivu-slider-disabled .ivu-slider-button {
  border-color: #808080 !important;
  background: #808080 !important;
  box-shadow: none !important;
  cursor: not-allowed;
}

/deep/ .ivu-slider-disabled .ivu-slider-button:hover {
  transform: none !important;
}

/* 相机配置样式 */
.camera-config-item {
  background: rgba(15, 35, 70, 0.8);
  border-color: rgba(0, 191, 255, 0.3);
}

.camera-config-item:hover {
  border-color: rgba(0, 191, 255, 0.5);
  box-shadow: 0 0 20px rgba(0, 191, 255, 0.15);
}

.camera-selection {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.camera-card {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(20, 45, 85, 0.6);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  user-select: none;
}

.camera-card:hover {
  border-color: rgba(0, 191, 255, 0.5);
  background: rgba(20, 45, 85, 0.8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.15);
}

.camera-card-selected {
  background: rgba(0, 255, 136, 0.12);
  border-color: rgba(0, 255, 136, 0.5);
  box-shadow: 0 0 12px rgba(0, 255, 136, 0.2);
}

.camera-card-selected:hover {
  border-color: rgba(0, 255, 136, 0.7);
  box-shadow: 0 4px 16px rgba(0, 255, 136, 0.3);
}

.camera-card-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(0.5);
}

.camera-card-disabled:hover {
  transform: none;
  border-color: rgba(0, 191, 255, 0.3);
  box-shadow: none;
}

.camera-card-icon {
  font-size: 18px;
  color: rgba(0, 191, 255, 0.8);
  transition: all 0.25s ease;
}

.camera-card:hover .camera-card-icon {
  color: rgba(0, 191, 255, 1);
}

.camera-card-selected .camera-card-icon {
  color: #00ff88;
}

.camera-card-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  transition: color 0.25s ease;
}

.camera-card:hover .camera-card-label {
  color: rgba(255, 255, 255, 0.95);
}

.camera-card-selected .camera-card-label {
  color: #00ff88;
}

.camera-card-check {
  position: absolute;
  top: -4px;
  right: -4px;
  font-size: 16px;
  color: #00ff88;
  filter: drop-shadow(0 0 4px rgba(0, 255, 136, 0.6));
  animation: check-appear 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes check-appear {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>